import React, { Suspense } from "react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrandLogo } from "@/components/branding/BrandLogo";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";

// All route pages lazy-loaded with retry to prevent startup blank screens on transient 503s

// Lazy-loaded pages via retry helper
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import { RouteLoadingFallback } from "@/components/RouteLoadingFallback";

const Index = lazyWithRetry(() => import("./pages/Index"));
const ProtectedAuth = lazyWithRetry(() => import("./pages/ProtectedAuth"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const About = lazyWithRetry(() => import("./pages/About"));
const LearnMore = lazyWithRetry(() => import("./pages/LearnMore"));
const SubscriptionSuccess = lazyWithRetry(() => import("./pages/SubscriptionSuccess"));
const SignupPlanSelection = lazyWithRetry(() => import("./pages/SignupPlanSelection"));
const SignupProfile = lazyWithRetry(() => import("./pages/SignupProfile"));
const AttorneyPlanSelection = lazyWithRetry(() => import("./pages/AttorneyPlanSelection"));
const SignupSuccess = lazyWithRetry(() => import("./pages/SignupSuccess"));
const AttorneySignupSuccess = lazyWithRetry(() => import("./pages/AttorneySignupSuccess"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Help = lazyWithRetry(() => import("./pages/Help"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const Usage = lazyWithRetry(() => import("./pages/Usage"));
const Account = lazyWithRetry(() => import("./pages/Account"));
const ClaudeConnectorSettings = lazyWithRetry(() => import("./pages/ClaudeConnectorSettings"));
const SecureDocumentViewer = lazyWithRetry(() => import("./pages/SecureDocumentViewer"));
const McpOAuthConsent = lazyWithRetry(() => import("./pages/McpOAuthConsent"));
const MCPPrivilegeNotice = lazyWithRetry(() => import("./pages/MCPPrivilegeNotice"));
const AttorneyPortal = lazyWithRetry(() => import("./pages/AttorneyPortal"));
const AttorneyMattersPortal = lazyWithRetry(() => import("./pages/AttorneyMattersPortal"));
const AttorneyLanding = lazyWithRetry(() => import("./pages/AttorneyLanding"));
const InHouseLanding = lazyWithRetry(() => import("./pages/InHouseLanding"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));

const ConnectionSuccess = lazyWithRetry(() => import("./pages/ConnectionSuccess"));
const JoinPage = lazyWithRetry(() => import("./pages/Join"));
const AttorneyCaseAcceptanceSuccess = lazyWithRetry(() => import("./pages/AttorneyCaseAcceptanceSuccess"));
const AttorneyDirectory = lazyWithRetry(() => import("./pages/AttorneyDirectory"));
const CoCounselDirectory = lazyWithRetry(() => import("./pages/CoCounselDirectory"));
const CoCounselCollaborationDemo = lazyWithRetry(() => import("./pages/CoCounselCollaborationDemo"));
const AttorneySignup = lazyWithRetry(() => import("./pages/AttorneySignup"));
const AccountTypeSelection = lazyWithRetry(() => import("./pages/AccountTypeSelection"));
const InHouseCounselPortal = lazyWithRetry(() => import("./pages/InHouseCounselPortal"));
const InHouseOnboarding = lazyWithRetry(() => import("./pages/InHouseOnboarding"));
const InHousePricing = lazyWithRetry(() => import("./pages/InHousePricing"));
const PublicIntake = lazyWithRetry(() => import("./pages/PublicIntake"));
const ForLawStudents = lazyWithRetry(() => import("./pages/ForLawStudents"));
const ClaimProfile = lazyWithRetry(() => import("./pages/ClaimProfile"));
const FindMyProfile = lazyWithRetry(() => import("./pages/FindMyProfile"));
const OAuthClientProfile = lazyWithRetry(() => import("./pages/OAuthClientProfile"));
const OAuthAttorneyProfile = lazyWithRetry(() => import("./pages/OAuthAttorneyProfile"));
const ClientPortalPage = lazyWithRetry(() => import("./pages/ClientPortal"));
const ClientDocumentUpload = lazyWithRetry(() => import("./pages/ClientDocumentUpload"));
const ClientConsultationDetailPage = lazyWithRetry(() =>
  import("./components/consultations/ClientConsultationDetailPage").then(m => ({ default: m.ClientConsultationDetailPage }))
);
const LawyerNDAConsultationDetailPage = lazyWithRetry(() =>
  import("./components/consultations/LawyerNDAConsultationDetailPage").then(m => ({ default: m.LawyerNDAConsultationDetailPage }))
);
const LawyerNDAConferralDetailPage = lazyWithRetry(() =>
  import("./components/nda/LawyerNDAConferralDetailPage").then(m => ({ default: m.LawyerNDAConferralDetailPage }))
);
const DelegateAccept = lazyWithRetry(() => import("./pages/DelegateAccept"));
const InHouseDelegateAccept = lazyWithRetry(() => import("./pages/InHouseDelegateAccept"));
const ClientSignup = lazyWithRetry(() =>
  import("./pages/ClientSignup").then(m => ({ default: m.ClientSignup }))
);
const BookingFrame = lazyWithRetry(() => import("./pages/BookingFrame"));
const Rewards = lazyWithRetry(() => import("./pages/Rewards"));
const NDAReview = lazyWithRetry(() => import("./pages/NDAReview"));
const NDASignedPage = lazyWithRetry(() => import("./pages/NDASignedPage"));
const ClientProposalReview = lazyWithRetry(() => import("./components/ClientProposalReview"));
const PayInvoice = lazyWithRetry(() => import("./pages/PayInvoice"));
const Demo = lazyWithRetry(() => import("./pages/Demo"));
const Features = lazyWithRetry(() => import("./pages/Features"));
const Launchpad = lazyWithRetry(() => import("./pages/Launchpad"));
const LaunchpadPricing = lazyWithRetry(() => import("./pages/LaunchpadPricing"));
// LaunchpadSignup removed — /launchpad/signup now redirects to
// /launchpad/pricing, which is the single Engage + Upgrade page.
const LaunchpadLanding = lazyWithRetry(() => import("./pages/LaunchpadLanding"));
const LaunchpadCounsel = lazyWithRetry(() => import("./pages/LaunchpadCounsel"));
const AuthConfirm = lazyWithRetry(() => import("./pages/AuthConfirm"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const SocialCallback = lazyWithRetry(() => import("./pages/SocialCallback"));

// Lazy-load heavier non-page modules
const ClientAgreementReview = lazyWithRetry(() =>
  import("./components/ClientAgreementReview").then(m => ({ default: m.ClientAgreementReview }))
);

const NetworkStatusBanner = lazyWithRetry(() =>
  import("./components/NetworkStatusBanner").then(m => ({ default: m.NetworkStatusBanner }))
);
const NotificationBellSheet = lazyWithRetry(() =>
  import("@/components/notifications/NotificationBellSheet").then(m => ({ default: m.NotificationBellSheet }))
);

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UsageProvider } from "@/context/UsageContext";
import { UserRoleProvider } from "@/context/UserRoleContext";
import { Button } from "@/components/ui/button";
import { useUserRole } from "@/hooks/useUserRole";
import { NotificationToastProvider } from "@/components/NotificationToastProvider";
import { MessagesActiveProvider } from "@/context/MessagesActiveContext";
const FloatingMenuButton = lazyWithRetry(() =>
  import("@/components/FloatingMenuButton").then(m => ({ default: m.FloatingMenuButton }))
);
import { ErrorBoundary } from "@/components/monitoring/ErrorBoundary";
const PerformanceMonitor = lazyWithRetry(() =>
  import("@/components/monitoring/PerformanceMonitor").then(m => ({ default: m.PerformanceMonitor }))
);
const UsageAnalytics = lazyWithRetry(() =>
  import("@/components/analytics/UsageAnalytics").then(m => ({ default: m.UsageAnalytics }))
);
import { FeatureFlagsProvider } from "@/components/monitoring/FeatureFlags";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PointerEventsGuard } from "@/components/PointerEventsGuard";
import { BodyScrollGuard } from "@/components/BodyScrollGuard";
import { resolvePortal, portalPath } from "@/lib/portalResolution";

const queryClient = new QueryClient();

// Component to handle attorney account redirect
const AttorneyAccountRedirect = () => {
  const {
    isAttorney,
    isAdmin,
    isInHouseCounsel,
    isDelegate,
    isClient,
  } = useUserRole();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const portal = resolvePortal({
    hasUser: !!user,
    isAttorney,
    isInHouseCounsel,
    isAdmin,
    isDelegate,
    isClient,
  });
  React.useEffect(() => {
    if (portal === 'in-house') {
      navigate('/in-house-portal?tab=account', { replace: true });
    } else if (portal === 'attorney') {
      const searchParams = new URLSearchParams(location.search);
      const scrollTo = searchParams.get('scrollTo');
      let targetTab = 'profile';
      if (scrollTo === 'subscription') {
        targetTab = 'subscription';
      } else if (scrollTo === 'billing') {
        targetTab = 'billing';
      }
      navigate(`/attorney-portal?tab=${targetTab}`, {
        replace: true
      });
    }
  }, [portal, navigate, location.search]);

  if (portal === 'attorney' || portal === 'in-house') {
    return <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Redirecting to {portal === 'in-house' ? 'In-House Portal' : 'Attorney Portal'}...</p>
        </div>
      </div>;
  }

  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Account />
    </Suspense>
  );
};

const ProtectedRoute = ({
  children,
  requireAttorney = false,
  requireAdmin = false,
  requireInHouse = false,
  redirectAttorneysToPortal = false,
  redirectAdminsToPortal = false,
  allowAnonymous = false
}: {
  children: React.ReactNode;
  requireAttorney?: boolean;
  requireAdmin?: boolean;
  requireInHouse?: boolean;
  redirectAttorneysToPortal?: boolean;
  redirectAdminsToPortal?: boolean;
  allowAnonymous?: boolean;
}) => {
  const { user, loading: authLoading } = useAuth();
  const {
    isAttorney,
    isAdmin,
    isInHouseCounsel,
    isDelegate,
    isClient,
    loading: roleLoading,
  } = useUserRole();

  if (authLoading || user && roleLoading) {
    return <div className="flex justify-center items-center py-8 bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>;
  }

  if (!user && !allowAnonymous) {
    const fullPath = window.location.pathname + window.location.search;
    if (fullPath !== '/auth' && window.location.search) {
      sessionStorage.setItem('deep_link_return_url', fullPath);
    }
    return <Navigate to="/auth" replace />;
  }

  // Single source of truth for portal resolution. The priority ladder
  // inside resolvePortal guarantees attorneys/in-house/admin/client never
  // flip between renders for the same user.
  const portal = resolvePortal({
    hasUser: !!user,
    isAttorney,
    isInHouseCounsel,
    isAdmin,
    isDelegate,
    isClient,
  });

  // When user is both admin AND attorney, prioritize attorney portal for auto-redirects.
  // The admin dashboard remains accessible via direct navigation to /admin-dashboard.
  if (redirectAttorneysToPortal && portal === 'attorney') {
    return <Navigate to="/attorney-portal" replace />;
  }
  if (redirectAdminsToPortal && portal === 'admin') {
    return <Navigate to="/admin-dashboard" replace />;
  }
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  if (requireInHouse && portal !== 'in-house') {
    return <Navigate to={portalPath(portal)} replace />;
  }
  if (requireAttorney && portal !== 'attorney') {
    return <Navigate to={portalPath(portal)} replace />;
  }
  return <>{children}</>;
};

const AppContent = () => {
  const location = useLocation();

  const portalRoutes = ['/attorney-portal', '/admin-dashboard', '/connection-success'];
  const shouldShowFloatingMenu = !portalRoutes.some((route) => location.pathname.startsWith(route)) &&
    location.pathname !== '/auth';
  const isPortalRoute = portalRoutes.some((route) => location.pathname.startsWith(route));
    
  return <div className="min-h-screen flex flex-col">
      <Suspense fallback={null}>
        {shouldShowFloatingMenu && <FloatingMenuButton />}
        <NotificationBellSheet />
      </Suspense>
      <main className="flex-1">
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            {/* Public homepage – eagerly loaded */}
            <Route path="/" element={<Index />} />
            <Route path="/for-clients" element={<LearnMore />} />
            <Route path="/learnmore" element={<Navigate to="/for-clients" replace />} />
            <Route path="/app" element={<ProtectedRoute redirectAttorneysToPortal redirectAdminsToPortal><Index /></ProtectedRoute>} />
            <Route path="/about" element={<About />} />
            <Route path="/for-attorneys" element={<AttorneyLanding />} />
            <Route path="/for-in-house" element={<InHouseLanding />} />
            <Route path="/for-law-students" element={<ForLawStudents />} />
            <Route path="/auth" element={<ProtectedAuth />} />
            <Route path="/auth/confirm" element={<ErrorBoundary><AuthConfirm /></ErrorBoundary>} />
            {/* Public sign-up is disabled — LexSavvy is invitation-only. */}
            <Route path="/signup" element={<Navigate to="/" replace />} />
            <Route path="/signup/plans" element={<Navigate to="/" replace />} />
            <Route path="/signup-plan-selection" element={<Navigate to="/" replace />} />
            <Route path="/signup/profile" element={<Navigate to="/" replace />} />
            <Route path="/signup/oauth-profile" element={<Navigate to="/" replace />} />
            <Route path="/signup/oauth-attorney-profile" element={<Navigate to="/" replace />} />
            <Route path="/signup/attorney" element={<Navigate to="/" replace />} />
            <Route path="/attorney-signup" element={<Navigate to="/" replace />} />
            <Route path="/signup/success" element={<SignupSuccess />} />
            <Route path="/signup/attorney-success" element={<AttorneySignupSuccess />} />
            <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/attorney-portal" element={<ProtectedRoute requireAttorney><AttorneyPortal /></ProtectedRoute>} />
            <Route path="/attorney-portal/consultations/nda/:id" element={<ProtectedRoute requireAttorney><LawyerNDAConsultationDetailPage /></ProtectedRoute>} />
            <Route path="/attorney-portal/nda/conferrals/:negotiationId" element={<ProtectedRoute requireAttorney><LawyerNDAConferralDetailPage /></ProtectedRoute>} />
            <Route path="/attorney-portal/co-counsel" element={<ProtectedRoute requireAttorney><CoCounselDirectory /></ProtectedRoute>} />
            <Route path="/attorney-portal/co-counsel-demo" element={<ProtectedRoute requireAttorney><CoCounselCollaborationDemo /></ProtectedRoute>} />
            <Route path="/attorney-matters-portal" element={<ProtectedRoute requireAttorney><AttorneyMattersPortal /></ProtectedRoute>} />
            <Route path="/admin-dashboard" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
            
            <Route path="/client-portal" element={<ClientPortalPage />} />
            <Route path="/launchpad" element={<ProtectedRoute><Launchpad /></ProtectedRoute>} />
            <Route path="/launchpad/pricing" element={<Navigate to="/" replace />} />
            <Route path="/launchpad/counsel" element={<LaunchpadCounsel />} />
            <Route path="/launchpad/signup" element={<Navigate to="/" replace />} />
            <Route path="/launchpad/welcome" element={<LaunchpadLanding />} />
            
            <Route path="/client-portal/consultations/:kind/:id" element={<ProtectedRoute><ClientConsultationDetailPage /></ProtectedRoute>} />
            <Route path="/in-house-portal" element={<ProtectedRoute requireInHouse><InHouseCounselPortal /></ProtectedRoute>} />
            <Route path="/signup/in-house" element={<ProtectedRoute requireInHouse><InHouseOnboarding /></ProtectedRoute>} />
            <Route path="/in-house/pricing" element={<Navigate to="/" replace />} />
            <Route path="/legal/intake/:slug" element={<PublicIntake />} />
            <Route path="/delegate-accept" element={<DelegateAccept />} />
            <Route path="/in-house-delegate-accept" element={<InHouseDelegateAccept />} />
            <Route path="/notifications" element={<Navigate to="/account" replace />} />
            <Route path="/account" element={<ProtectedRoute><AttorneyAccountRedirect /></ProtectedRoute>} />
            <Route path="/settings/claude-connector" element={<ProtectedRoute><ClaudeConnectorSettings /></ProtectedRoute>} />
            <Route path="/secure-viewer/:kind/:id" element={<ProtectedRoute requireAttorney><SecureDocumentViewer /></ProtectedRoute>} />
            <Route path="/oauth/mcp/consent" element={<ProtectedRoute><McpOAuthConsent /></ProtectedRoute>} />
            <Route path="/legal/mcp-privilege" element={<MCPPrivilegeNotice />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/client-agreement" element={<ProtectedRoute><ClientAgreementReview /></ProtectedRoute>} />
            <Route path="/client-signup" element={<Navigate to="/" replace />} />
            <Route path="/upload/:token" element={<ClientDocumentUpload />} />
            <Route path="/help" element={<Help />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/usage" element={<ProtectedRoute><Usage /></ProtectedRoute>} />
            <Route path="/attorney-directory" element={<AttorneyDirectory />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/connection-success" element={<ConnectionSuccess />} />
            <Route path="/booking/:attorneyId" element={<BookingFrame />} />
            <Route path="/attorney-case-acceptance-success" element={<ProtectedRoute requireAttorney><AttorneyCaseAcceptanceSuccess /></ProtectedRoute>} />
            <Route path="/client/agreement/:agreementId" element={<ClientAgreementReview />} />
            <Route path="/client/agreement/review" element={<ClientAgreementReview />} />
            <Route path="/client/proposal/:proposalId" element={<ClientProposalReview />} />
            <Route path="/nda/review/:token" element={<NDAReview />} />
            <Route path="/nda/signed" element={<NDASignedPage />} />
            <Route path="/pay-invoice" element={<PayInvoice />} />
            <Route path="/rewards" element={<Rewards />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/features" element={<Features />} />
            <Route path="/claim-profile" element={<Navigate to="/" replace />} />
            <Route path="/find-my-profile" element={<Navigate to="/" replace />} />
            <Route path="/social-callback" element={<SocialCallback />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
    </div>;
};

const App = () => {
  return <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange>
      <AuthProvider>
        <UserRoleProvider>
          <UsageProvider>
            <BrowserRouter>
              <ScrollToTop />
              <PointerEventsGuard />
              <BodyScrollGuard />
              <TooltipProvider>
                <FeatureFlagsProvider>
                  <ErrorBoundary>
                    <MessagesActiveProvider>
                      <NotificationToastProvider>
                        <Suspense fallback={null}>
                          <PerformanceMonitor />
                          <UsageAnalytics />
                          <NetworkStatusBanner />
                        </Suspense>
                        <Toaster />
                        <SonnerToaster />
                        <AppContent />
                      </NotificationToastProvider>
                    </MessagesActiveProvider>
                  </ErrorBoundary>
                </FeatureFlagsProvider>
              </TooltipProvider>
            </BrowserRouter>
          </UsageProvider>
        </UserRoleProvider>
      </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>;
};
export default App;
